import { useEffect, useState } from 'react';

/**
 * Where the feedback button goes while a drawer or dialog is open (ALS-458).
 *
 * - `none`: nothing is open, so the button keeps its corner.
 * - `dialog`: a centred dialog is open and leaves the corner free.
 * - `beside-drawer`: a drawer is open and there is room on its left. `right` is the drawer's
 *   width, measured from the right edge of the screen.
 * - `above-footer`: a drawer fills the screen, as on a phone, or a tall dialog reaches the
 *   corner. `bottom` is how far the top of its footer is from the bottom of the screen, so
 *   the button floats over the overlay's content and never over Save or Cancel.
 */
export type OverlayPlacement =
    | { kind: 'none' }
    | { kind: 'dialog' }
    | { kind: 'beside-drawer'; right: number }
    | { kind: 'above-footer'; bottom: number };

const NONE: OverlayPlacement = { kind: 'none' };

// Read from the attributes Radix and our Sheet and Dialog already put on an open overlay, so
// nothing in those components has to know the button exists.
const OPEN_DRAWER = '[data-slot="sheet-content"][data-state="open"]';
const OPEN_DIALOG =
    '[data-slot="dialog-content"][data-state="open"], [data-slot="alert-dialog-content"][data-state="open"]';
const DRAWER_FOOTER = '[data-slot="sheet-footer"]';
const DIALOG_FOOTER = '[data-slot="dialog-footer"], [data-slot="alert-dialog-footer"]';

// The button is size-11, and sits 1rem from whatever it is beside.
const BUTTON = 44;
const GUTTER = 16;

function footerHeight(overlay: HTMLElement, selector: string): number {
    return overlay.querySelector<HTMLElement>(selector)?.offsetHeight ?? 0;
}

/**
 * Measures the open overlay. Sizes come from `offsetWidth` and `offsetHeight` rather than
 * the bounding box, because overlays open with a transform (a drawer slides in, a dialog
 * zooms in) and the box moves with it.
 *
 * Every drawer in the admin opens from the right (the Sheet's default side), so its width is
 * how far the button moves left. Dialogs are centred, so their edges follow from their size:
 * a short one leaves the corner free, while a tall one on a phone (a banded dialog grows to
 * the screen's height less 48px) reaches it, and the button goes above its footer.
 */
export function measureOverlay(): OverlayPlacement {
    const drawers = document.querySelectorAll<HTMLElement>(OPEN_DRAWER);
    const drawer = drawers[drawers.length - 1];
    if (drawer) {
        const width = drawer.offsetWidth;
        if (window.innerWidth >= width + BUTTON + 2 * GUTTER) {
            return { kind: 'beside-drawer', right: width };
        }
        return { kind: 'above-footer', bottom: footerHeight(drawer, DRAWER_FOOTER) };
    }

    const dialogs = document.querySelectorAll<HTMLElement>(OPEN_DIALOG);
    const dialog = dialogs[dialogs.length - 1];
    if (!dialog) return NONE;
    const corner = BUTTON + GUTTER;
    const gapRight = (window.innerWidth - dialog.offsetWidth) / 2;
    const gapBelow = Math.max(0, (window.innerHeight - dialog.offsetHeight) / 2);
    if (gapRight >= corner || gapBelow >= corner) return { kind: 'dialog' };
    return {
        kind: 'above-footer',
        bottom: Math.round(gapBelow + footerHeight(dialog, DIALOG_FOOTER)),
    };
}

function samePlacement(a: OverlayPlacement, b: OverlayPlacement): boolean {
    if (a.kind !== b.kind) return false;
    if (a.kind === 'beside-drawer' && b.kind === 'beside-drawer') return a.right === b.right;
    if (a.kind === 'above-footer' && b.kind === 'above-footer') return a.bottom === b.bottom;
    return true;
}

/**
 * Follows the drawers and dialogs that open and close anywhere in the admin, and where the
 * feedback button should sit meanwhile. Watches only while `enabled`, which is while Shake
 * runs: on the dev server and in the e2e suite there is no button to place.
 */
export function useOpenOverlay(enabled: boolean): OverlayPlacement {
    const [placement, setPlacement] = useState<OverlayPlacement>(NONE);

    useEffect(() => {
        if (!enabled) return;

        const update = () => {
            const next = measureOverlay();
            setPlacement((current) => (samePlacement(current, next) ? current : next));
        };

        update();
        // Overlays are portalled into <body> and flip data-state as they open and close.
        const observer = new MutationObserver(update);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-state'],
        });
        window.addEventListener('resize', update);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [enabled]);

    return enabled ? placement : NONE;
}
